import { useTranslation } from "react-i18next";

import { ErrorView } from "@/components/_molecules/error-view";
import { Loading } from "@/components/_molecules/loading-view";
import { useDomainTranslation } from "@/domain/_shared/i18n/use-domain-translation";
import { MunicipalPropertyRegisterFilterBar } from "@/domain/municipal-property/components/municipal-property-register-filter-bar";
import { MunicipalPropertyRegisterTable } from "@/domain/municipal-property/components/municipal-property-register-table";
import { useMunicipalPropertyRegisterList } from "@/domain/municipal-property/hooks/api/use-municipal-property-register-list";
import { useDebouncedSearchFilter } from "@/domain/municipal-property/hooks/use-debounced-search-filter";
import { EMPTY_MUNICIPAL_PROPERTY_REGISTER_FILTER } from "@/domain/municipal-property/models/empty-municipal-property-register-filter";
import { MunicipalPropertyRegisterFilter } from "@/domain/municipal-property/models/municipal-property-register-filter.model";
import { useCurrentWorkspaceContext } from "@/domain/workspace/hooks/useCurrentWorkspaceContext";

export const MunicipalPropertyRegisterListPage = () => {
  const { currentWorkspace } = useCurrentWorkspaceContext();
  const { t } = useDomainTranslation("municipal-property");
  const { t: tCommon } = useTranslation("common");
  const { filter, setFilter, displayedFilter, handleSearchChange }: ReturnType<typeof useDebouncedSearchFilter<MunicipalPropertyRegisterFilter>> =
    useDebouncedSearchFilter<MunicipalPropertyRegisterFilter>(EMPTY_MUNICIPAL_PROPERTY_REGISTER_FILTER);

  const { registerList, totalCount, isLoading, error, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useMunicipalPropertyRegisterList({ workspaceId: currentWorkspace.id, filter });

  const hasLoadedRows = registerList.length > 0;
  const isFiltered: boolean = filter.year !== null || !!filter.search?.trim();

  return (
    <div className="flex h-full max-h-full flex-col overflow-y-hidden">
      <MunicipalPropertyRegisterFilterBar
        filter={displayedFilter}
        onSearchChange={handleSearchChange}
        onYearChange={(year) => setFilter((current) => ({ ...current, year }))}
      />
      {isLoading ? <Loading /> : null}
      {!!error && !hasLoadedRows ? <ErrorView /> : null}
      {!!error && hasLoadedRows ? (
        <p role="alert" className="px-3 py-2 text-sm text-destructive">
          {tCommon("errors.pageError")}
        </p>
      ) : null}
      {!isLoading && !error && !hasLoadedRows ? (
        <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
          {isFiltered ? t("register.emptyFiltered") : t("register.empty")}
        </div>
      ) : null}
      {!isLoading && hasLoadedRows ? (
        <MunicipalPropertyRegisterTable
          registerList={registerList}
          totalCount={totalCount}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          onLoadMore={() => void fetchNextPage()}
        />
      ) : null}
    </div>
  );
};
